'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { MdCheck, MdCode } from 'react-icons/md';

import { cn } from '@/lib/styleUtils';
import { DraggableCar } from '@/modules/captcha/components/CarSprite';
import { PuzzlePrompt, PuzzleScene } from '@/modules/captcha/components/PuzzleFrame';
import { SLIDER_TRAVEL, usePuzzleSlider } from '@/modules/captcha/hooks/usePuzzleSlider';
import { BAY_LEFTS, randomInt } from '@/modules/captcha/shared/puzzle-utils';
import {
  sliderHandle,
  sliderLabel,
  sliderTrack,
  slotBase,
  slotCorrect,
  slotIdle,
  slotTarget,
} from './puzzle.style';
import type { PuzzleProps } from './puzzle.types';

const SCENE_HEIGHT = 186;

/**
 * Half-width of the accepted landing zone, in px of the 312px scene.
 *
 * 12px against a 66px bay is roughly a fifth of the bay — tight enough that the customer has to
 * aim, loose enough to clear on a phone without pixel-hunting. This is the one puzzle in the set
 * that asks for precision, so the tolerance is the whole difficulty dial: shrinking it makes the
 * challenge unpassable on a small screen, widening it makes the slider a slide-to-end.
 */
const ALIGN_TOLERANCE = 12;

/**
 * Design 1d — slide the car until it lines up with the one empty bay, then release.
 *
 * ## Invariants:
 * - The empty bay is rerolled per challenge, and the occupied bays are drawn in a flat grey
 *   distinct from the tenant's primary. Why: the moving car is the only branded element, so the
 *   target reads as "the space", not "the odd colour".
 */
export const FillTheGap = ({ onSolved }: PuzzleProps) => {
  const t = useTranslations('Captcha');
  const [gap] = useState(() => randomInt(BAY_LEFTS.length));

  const gapCenterX = BAY_LEFTS[gap] + 33;

  const onRelease = useCallback(
    (progress: number) => {
      const carCenterX = progress * SLIDER_TRAVEL + 23;
      if (Math.abs(carCenterX - gapCenterX) > ALIGN_TOLERANCE) return false;
      onSolved();
      return true;
    },
    [gapCenterX, onSolved]
  );

  const { trackRef, progress, sliding, accepted, handlers } = usePuzzleSlider({
    onRelease,
    // Snap the car to dead centre of the bay once accepted, so the resting state looks parked
    // rather than "close enough".
    snapTo: () => (gapCenterX - 23) / SLIDER_TRAVEL,
    // A near miss keeps the car where the customer left it. They aimed at a specific bay and can
    // see how far off they were; springing back to the start would make them re-aim from scratch
    // every attempt, and would leave a keyboard user stepping forward and being reset forever.
    resetOnReject: false,
  });

  const handleX = Math.round(progress * SLIDER_TRAVEL);
  const carX = accepted ? BAY_LEFTS[gap] + 10 : handleX;
  const carY = accepted ? 23 : 112;

  return (
    <>
      <PuzzlePrompt>{t('fillTheGapPrompt')}</PuzzlePrompt>

      <PuzzleScene height={SCENE_HEIGHT} data-cy="captcha-fill-the-gap">
        {BAY_LEFTS.map((left, index) => {
          const isGap = index === gap;
          return (
            <div
              key={left}
              className={cn(
                slotBase,
                'tw:top-micro tw:h-[96px] tw:w-[66px]',
                !isGap && slotIdle,
                isGap && (accepted ? slotCorrect : slotTarget)
              )}
              style={{ left }}
              data-cy={`captcha-gap-bay-${index}`}
            >
              {!isGap && (
                <div
                  className="tw:absolute tw:left-[9px] tw:top-[14px] tw:h-[64px] tw:w-[44px] tw:rounded-[12px_12px_9px_9px] tw:bg-gray"
                  aria-hidden="true"
                >
                  <div className="tw:absolute tw:left-[6px] tw:right-[6px] tw:top-small tw:h-small tw:rounded-4 tw:bg-gray-light" />
                  <div className="tw:absolute tw:bottom-[7px] tw:left-micro tw:right-micro tw:h-[7px] tw:rounded-2 tw:bg-gray-light tw:opacity-80" />
                </div>
              )}
            </div>
          );
        })}
        <div
          className="tw:absolute tw:left-0 tw:top-0 tw:z-2 tw:h-[66px] tw:w-[46px]"
          style={{
            transform: `translate(${carX}px, ${carY}px)`,
            transition: sliding ? 'none' : `transform ${accepted ? '0.5s' : '0.2s'} ease`,
          }}
          aria-hidden="true"
        >
          <DraggableCar />
        </div>
      </PuzzleScene>

      <div ref={trackRef} className={sliderTrack} data-cy="captcha-gap-track">
        <span className={sliderLabel}>{t('fillTheGapSliderLabel')}</span>
        <div
          {...handlers}
          role="slider"
          tabIndex={0}
          aria-label={t('fillTheGapSliderLabel')}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
          className={sliderHandle}
          style={{
            transform: `translateX(${handleX}px)`,
            transition: sliding ? 'none' : 'transform 0.3s ease',
          }}
          data-cy="captcha-gap-handle"
        >
          {accepted ? (
            <MdCheck size={18} aria-hidden="true" />
          ) : (
            <MdCode size={18} aria-hidden="true" />
          )}
        </div>
      </div>
    </>
  );
};

FillTheGap.displayName = 'FillTheGap';
